// src/pages/project/ProjectDashboard.jsx

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import WeatherComponent from "../../components/widgets/WeatherWidget";
import ThreeViewerWithTimeline from "../../components/viewer/viewer3d/ThreeViewerWithTimeline";
import AgentChatbot from "../../components/const/MobileAgentChat";
import ApprovalWidget from "../../components/widgets/ApprovalWidget";
import DDayCounter from "../../components/widgets/mobile_DDayCounter";
import ProgressStatusWidget from "../../components/widgets/ProgressStatusWidget";
import MilestoneSchedule from "../../components/widgets/mobile_MilestoneSchedule";
import NoticeWidget from "../../components/widgets/NoticeWidget";
import MiniSafetySignal from "../../components/widgets/MiniSafetyWidget";
import ConstPhotoWidget from "../../components/widgets/ConstructionPhotoWidget";
import TodoWidget from "../../components/widgets/TodoWidget";
import SafetyStatusStrip from "../../components/widgets/SafetyStatusStrip";
// P6ixChatbot removed - using MobileAgentChat only

import {
  ShieldCheck,
  Factory,
  Truck,
  AlertTriangle,
  Settings,
  HardHat,
  Zap,
  X,
} from "lucide-react";

import { getProjectSummary } from "../../api/project";
import { getPapersTree } from "../../api/papers";
import WidgetCard from "../../components/widgets/WidgetCard";

// ✅ 모바일 버전과 동일하게 showNotice, showChatbot 포함
const DEFAULT_SETTINGS = {
  showSummaryCards: true,
  showDDayAndMilestone: true,
  showWeather: true,
  showApproval: true,
  showFourDPreview: false, // 4D 시뮬레이션 미리보기 임시 비활성화
  showChatbot: true,
  showConstPhotos: true,
  showNotice: true,
};

function ProjectDashboard() {
  const { id: projectId } = useParams();

  const [projectname, setProjectname] = useState("-");
  const [noIncidentDays, setNoIncidentDays] = useState("");
  const [lastIncidentDate, setLastIncidentDate] = useState("");

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // P6ix AI Bottom Sheet 상태
  const [isP6ixOpen, setIsP6ixOpen] = useState(false);

  // Agent Chatbot 상태
  const [isAgentOpen, setIsAgentOpen] = useState(false);

  // 🔒 4D glTF 관련 (임시 비활성화)
  // const [gltfFiles, setGltfFiles] = useState([]);
  // const [selectedGltf, setSelectedGltf] = useState(null);

  // 📌 프로젝트 요약 (이름, KPI)
  useEffect(() => {
    const loadProjectSummary = async () => {
      try {
        const res = await getProjectSummary();
        setProjectname(res.project.name);

        const days = res.kpi.no_incident_days;
        setNoIncidentDays(
          days !== null && days !== undefined ? `${days}일` : "-"
        );
        setLastIncidentDate(res.kpi.last_incident_date || "사고 이력 없음");
      } catch (err) {
        console.error("프로젝트 요약 불러오기 실패", err);
      }
    };
    loadProjectSummary();
  }, [projectId]);

  // � 자료실에서 4DSimulation 폴더만 찾아서 glTF 목록 구성 (임시 비활성화)
  // useEffect(() => {
  //   const loadFourDFromPapers = async () => {
  //     if (!projectId) return;

  //     try {
  //       const res = await getPapersTree(projectId);
  //       const roots = res.data || [];

  //       const findFolderByName = (folders, targetName) => {
  //         for (const folder of folders) {
  //           if (folder.name === targetName) return folder;
  //           const child = findFolderByName(folder.children || [], targetName);
  //           if (child) return child;
  //         }
  //         return null;
  //       };

  //       const folder4D = findFolderByName(roots, "4DSimulation");
  //       if (!folder4D) {
  //         setGltfFiles([]);
  //         setSelectedGltf(null);
  //         return;
  //       }

  //       const collectGltfFiles = (folder, acc = []) => {
  //         const files = folder.files || [];
  //         files.forEach((f) => {
  //           const ext = f.name?.split(".").pop()?.toLowerCase() || "";
  //           if (ext === "gltf") acc.push(f);
  //         });
  //         (folder.children || []).forEach((ch) => collectGltfFiles(ch, acc));
  //         return acc;
  //       };

  //       const gltfs = collectGltfFiles(folder4D, []);
  //       setGltfFiles(gltfs);
  //       setSelectedGltf(gltfs.length > 0 ? gltfs[0] : null);
  //     } catch (err) {
  //       console.error("자료실 4DSimulation glTF 로드 실패", err);
  //       setGltfFiles([]);
  //       setSelectedGltf(null);
  //     }
  //   };

  //   loadFourDFromPapers();
  // }, [projectId]);

  // 📌 프로젝트별 대시보드 설정 로드 (localStorage)
  useEffect(() => {
    if (!projectId) return;
    try {
      const raw = localStorage.getItem(`dashboardSettings_${projectId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      } else {
        setSettings(DEFAULT_SETTINGS);
      }
    } catch (e) {
      console.error("대시보드 설정 로드 실패", e);
      setSettings(DEFAULT_SETTINGS);
    }
  }, [projectId]);

  // 설정 저장
  const updateSetting = (key) => (e) => {
    const next = {
      ...settings,
      [key]: e.target.checked,
    };
    setSettings(next);
    if (projectId) {
      localStorage.setItem(
        `dashboardSettings_${projectId}`,
        JSON.stringify(next)
      );
    }
  };

  return (
    // 모바일 UX 기준 레이아웃 + 가로 스크롤 방지
    <div className="p-3 md:p-4 space-y-4 md:space-y-6 mb-16 bg-slate-50 min-h-screen overflow-x-hidden">
      {/* 상단 프로젝트 정보 + 설정 버튼 (모바일 버전 반영) */}
      {/* <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] md:text-[12px] text-slate-500 truncate">
            현재 프로젝트 ID: <strong>{projectId}</strong>
          </p>
          <p className="font-bold text-slate-800 text-[15px] md:text-[17px] mt-0.5 truncate">
            {projectname}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSettingsOpen((prev) => !prev)}
          className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 md:px-3 py-1 bg-white hover:bg-slate-50"
        >
          <Settings className="w-4 h-4" />
          <span className="text-[11px] md:text-[12px] text-slate-700">
            위젯 설정
          </span>
        </button>
      </div> */}

      {/* 위젯 설정 패널 */}
      {settingsOpen && (
        <div className="border rounded-xl bg-white shadow-sm p-3 md:p-4 text-slate-700 space-y-2">
          <p className="font-semibold mb-1 text-[12px] md:text-[13px]">
            표시할 위젯 선택
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-y-1.5 gap-x-2">
            <CheckboxRow
              label="요약 카드"
              checked={settings.showSummaryCards}
              onChange={updateSetting("showSummaryCards")}
            />
            <CheckboxRow
              label="D-Day / 마일스톤"
              checked={settings.showDDayAndMilestone}
              onChange={updateSetting("showDDayAndMilestone")}
            />
            <CheckboxRow
              label="날씨"
              checked={settings.showWeather}
              onChange={updateSetting("showWeather")}
            />
            <CheckboxRow
              label="공지"
              checked={settings.showNotice}
              onChange={updateSetting("showNotice")}
            />
            <CheckboxRow
              label="결재"
              checked={settings.showApproval}
              onChange={updateSetting("showApproval")}
            />
            <CheckboxRow
              label="시공 사진"
              checked={settings.showConstPhotos}
              onChange={updateSetting("showConstPhotos")}
            />
            {/* 🔒 4D 미리보기 옵션 (임시 비활성화) */}
            {/* <CheckboxRow
              label="4D 미리보기"
              checked={settings.showFourDPreview}
              onChange={updateSetting("showFourDPreview")}
            /> */}
            <CheckboxRow
              label="AI 챗봇"
              checked={settings.showChatbot}
              onChange={updateSetting("showChatbot")}
            />
          </div>
        </div>
      )}

      {/* 1행: 요약 카드 (모바일 2열, 큰 화면에서는 3~4열) */}
      {/* {settings.showSummaryCards && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
          <SummaryCard
            title="무사고 일수"
            value={noIncidentDays}
            subtitle={lastIncidentDate}
            icon={
              <ShieldCheck className="text-green-600 w-5 h-5 md:w-6 md:h-6" />
            }
            color="text-green-600"
          />
          <SummaryCard
            title="제작 공장 가동률"
            value="85%"
            icon={<Factory className="text-blue-600 w-5 h-5 md:w-6 md:h-6" />}
            color="text-blue-600"
          />
          <SummaryCard
            title="물류 운행"
            value="4대 배송 중"
            icon={<Truck className="text-amber-600 w-5 h-5 md:w-6 md:h-6" />}
            color="text-amber-600"
          />
          <SummaryCard
            title="이슈 / 품질"
            value="3건"
            icon={
              <AlertTriangle className="text-red-600 w-5 h-5 md:w-6 md:h-6" />
            }
            color="text-red-600"
          />
          <SummaryCard
            title="안전 신호등"
            value={<MiniSafetySignal />}
            icon={<HardHat className="text-yellow-500 w-5 h-5 md:w-6 md:h-6" />}
            color="text-slate-600"
          />
        </div>
      )} */}

      {/* 2행: 안전 신호등 + 날씨 + D-Day + 마일스톤 (모바일 기준 세로 스택) */}
      {settings.showDDayAndMilestone && (
        <div className="space-y-3">
          {/* 🔴 오늘의 날씨 바로 위 안전 신호등 */}
          <div className="rounded-xl shadow-sm">
            <SafetyStatusStrip projectId={projectId} />
          </div>

          {settings.showWeather && (
            <div className="rounded-xl overflow-hidden">
              <WeatherComponent />
            </div>
          )}
          <div className="rounded-xl bg-white shadow-sm p-2 md:p-3">
            <DDayCounter />
          </div>
          {/* 공정 진행률 / 기성현황 */}
          <div className="rounded-xl bg-white shadow-sm p-2 md:p-3">
            <ProgressStatusWidget />
          </div>
          <div className="rounded-xl bg-white shadow-sm p-2 md:p-3">
            <MilestoneSchedule />
          </div>
        </div>
      )}

      {/* 3행: 공지 / 결재 / 시공사진 / TODO (세로 스택) */}
      <div className="space-y-3">
        {settings.showNotice && (
          <div className="rounded-xl bg-white shadow-sm p-2 md:p-3">
            <NoticeWidget />
          </div>
        )}
        {settings.showApproval && (
          <div className="rounded-xl bg-white shadow-sm p-2 md:p-3">
            <ApprovalWidget />
          </div>
        )}
        {settings.showConstPhotos && (
          <div className="rounded-xl bg-white shadow-sm p-2 md:p-3">
            <ConstPhotoWidget />
          </div>
        )}
        <div className="rounded-xl bg-white shadow-sm p-2 md:p-3">
          <TodoWidget />
        </div>
      </div>

      {/* 🔒 4행: 4D 시뮬레이션 미리보기 (임시 비활성화) */}
      {/* {settings.showFourDPreview && (
        <div className="mt-4">
          <h3 className="font-semibold mb-2 text-[15px] md:text-[17px]">
            4D 시뮬레이션 미리보기
          </h3>

          {selectedGltf ? (
            <div className="border rounded-xl shadow bg-white flex flex-col overflow-hidden">
              <div className="relative w-full pb-[46%]">
                <div className="absolute inset-0">
                  <ThreeViewerWithTimeline gltfUrl={selectedGltf.file} />
                </div>
              </div>

              {gltfFiles.length > 1 && (
                <div className="border-t px-3 py-2 flex flex-wrap gap-2 bg-slate-50">
                  <span className="text-[11px] text-slate-500 mt-1">
                    사용할 4D 모델 선택:
                  </span>
                  {gltfFiles.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setSelectedGltf(f)}
                      className={`px-2 py-1 rounded-full border transition text-[11px] ${
                        selectedGltf.id === f.id
                          ? "bg-blue-50 border-blue-400 text-blue-700"
                          : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {f.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-[12px] text-slate-500">
              자료실의 4DSimulation 폴더에 glTF 파일이 없습니다.
              <br />
              4D 업로드 페이지에서 모델을 먼저 등록하세요.
            </p>
          )}
        </div>
      )} */}

      {/* 5행: 챗봇 */}
      {settings.showChatbot && (
        <div className="mt-4">
          <AgentChatbot onOpenChange={setIsAgentOpen} />
        </div>
      )}

      {/* Footer: 회사 정보 */}
      <footer className="mt-8 pt-6 pb-4 border-t border-slate-200 bg-white rounded-t-xl">
        <div className="text-center space-y-2">
          {/* 주소 */}
          <p className="text-[11px] md:text-[12px] text-slate-600 leading-relaxed">
            서울특별시 금천구 가산디지털1로 128 STX V-Tower 910호
          </p>

          {/* 연락처 */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-3 text-[10px] md:text-[11px] text-slate-500">
            <span>Tel: 02-6337-1771</span>
            <span className="hidden sm:inline">|</span>
            <span>Fax: 02-3281-4067</span>
            <span className="hidden sm:inline">|</span>
            <a
              href="mailto:p6ix@p6ix.co.kr"
              className="text-blue-600 hover:text-blue-700 transition-colors"
            >
              Mail: p6ix@p6ix.co.kr
            </a>
          </div>

          {/* 저작권 */}
          <p className="text-[10px] md:text-[11px] text-slate-400 pt-2">
            Copyright© 2013. 피식스에스씨 All Rights reserved.
          </p>
        </div>
      </footer>

      {/* P6ix AI - MobileAgentChat에서 처리됨 */}
    </div>
  );
}

/* 체크박스 한 줄 컴포넌트 (모바일 버전 스타일 그대로) */
function CheckboxRow({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-1.5 text-[11px] md:text-[12px]">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="rounded border-slate-300 w-3.5 h-3.5"
      />
      <span>{label}</span>
    </label>
  );
}

/* SummaryCard – 모바일용 레이아웃 + 데스크탑 대응 폰트 */
function SummaryCard({ title, value, subtitle, icon, color }) {
  return (
    <WidgetCard title={title} className="shadow-sm">
      <div className="flex items-center gap-3">
        {/* 아이콘 */}
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100">
          {icon}
        </div>

        {/* 텍스트 */}
        <div className="min-w-0">
          <p
            className={`font-semibold text-[clamp(14px,2vw,18px)] leading-tight ${color}`}
          >
            {value}
          </p>

          {subtitle && (
            <p className="text-[clamp(10px,1.5vw,12px)] text-slate-500 mt-1 whitespace-normal break-keep">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </WidgetCard>
  );
}

export default ProjectDashboard;
